import React, { useMemo, useState } from "react";
import { getCalculatorCategoryName } from "../../data/calculatorCatalog";
import {
  PROFESSIONAL_CALCULATORS,
  PROFESSIONAL_CALCULATOR_LABELS,
} from "../../data/professionalCalculatorRegistry";
import {
  loadWorkspacePreferences,
  saveWorkspacePreferences,
} from "../../utils/workspacePreferences";
import {
  AdvancedOptionsPanel,
  AssumptionsPanel,
  CalculationTracePanel,
  ShopNotesPanel,
} from "../AdvancedCalculatorPanels";
import ManufacturingDiagram from "../ManufacturingDiagram";

const extractFirstNumber = (value) => {
  const match = String(value ?? "").match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const getAdvancedAdjustedValues = (values, advancedParams) => {
  const safetyFactor = Number(advancedParams.safetyFactor || 1);
  const allowance = Number(advancedParams.allowancePercent || 0) / 100;
  if (safetyFactor === 1 && allowance === 0) return {};

  return Object.entries(values || {}).reduce((next, [label, value]) => {
    const numeric = extractFirstNumber(value);
    if (!Number.isFinite(numeric)) return next;
    const adjusted = numeric * safetyFactor * (1 + allowance);
    next[`${label} with Advanced Allowance`] = adjusted.toFixed(Number(advancedParams.resultPrecision || 4));
    return next;
  }, {});
};

const getAdvancedShopNotes = (config, advancedParams) => {
  const notes = [
    "Advanced values are decision-support adjustments layered on top of the base formula, not a replacement for print requirements or shop standards.",
  ];
  if (Number(advancedParams.safetyFactor) !== 1) notes.push(`Safety factor ${advancedParams.safetyFactor} is applied to numeric result checks where a conservative envelope is useful.`);
  if (Number(advancedParams.allowancePercent) > 0) notes.push(`${advancedParams.allowancePercent}% scrap/rework/stock allowance is included in adjusted numeric estimates.`);
  if (Number(advancedParams.gaugeResolution) > 0) notes.push(`Gauge resolution is set to ${advancedParams.gaugeResolution}; inspection decisions should account for measurement uncertainty.`);
  if (config.category === "inspection") notes.push("For inspection calculators, verify datum scheme, MMC/LMC modifiers, gauge repeatability, and company acceptance rules.");
  if (config.category === "shop-business") notes.push("For quoting calculators, compare margin and markup carefully; they are not the same business metric.");
  if (config.category === "sheet-metal") notes.push("For formed parts, confirm K-factor, springback, grain direction, and tooling chart data before releasing flats.");
  if (config.category === "welding") notes.push("For weld calculators, heat input, filler use, and distortion depend on joint fit-up, position, process, and operator technique.");
  return notes;
};

const ProfessionalCalculator = ({ toolId, onSave, activePlanId }) => {
  const config = PROFESSIONAL_CALCULATORS[toolId];
  const [params, setParams] = useState(config?.initial || {});
  const [mode, setMode] = useState(() => loadWorkspacePreferences(activePlanId || "user").mode || "advanced");
  const [advancedParams, setAdvancedParams] = useState({
    safetyFactor: 1,
    allowancePercent: 0,
    gaugeResolution: "",
    resultPrecision: 4,
  });
  const result = useMemo(() => config?.calculate(params) || { errors: ["Calculator not configured."] }, [config, params]);
  const advancedAdjustedValues = useMemo(
    () => getAdvancedAdjustedValues(result.values, advancedParams),
    [result.values, advancedParams]
  );
  const advancedTrace = useMemo(
    () => [
      `Base calculator: ${config?.title || toolId}`,
      `Safety factor: ${advancedParams.safetyFactor}`,
      `Allowance percent: ${advancedParams.allowancePercent}%`,
      `Gauge resolution: ${advancedParams.gaugeResolution || "not entered"}`,
      `Display precision: ${advancedParams.resultPrecision} decimals`,
    ],
    [advancedParams, config, toolId]
  );
  const hasErrors = Array.isArray(result.errors) && result.errors.length > 0;

  if (!config) {
    return (
      <div className="calculator-page">
        <div className="calculator-shell">
          <div className="calculator-panel">
            <h1>Calculator not configured</h1>
          </div>
        </div>
      </div>
    );
  }

  const updateField = (key, value, type) => {
    setParams((current) => ({
      ...current,
      [key]: type === "number" ? value : value,
    }));
  };

  const updateAdvancedField = (key, value) => {
    setAdvancedParams((current) => ({ ...current, [key]: value }));
  };

  const reset = () => setParams(config.initial);

  const toggleMode = () => {
    const nextMode = mode === "beginner" ? "advanced" : "beginner";
    setMode(nextMode);
    saveWorkspacePreferences(activePlanId || "user", {
      ...loadWorkspacePreferences(activePlanId || "user"),
      mode: nextMode,
    });
  };

  const resultText = () => {
    if (hasErrors) return `${config.title}\n${result.errors.join("\n")}`;
    return [
      config.title,
      config.description,
      "",
      ...Object.entries(result.values || {}).map(([label, value]) => `${label}: ${value}`),
      "",
      "Formulas:",
      ...(result.formulas || []),
      "",
      "Assumptions:",
      ...(result.assumptions || config.assumptions || []),
      ...(result.warnings?.length ? ["", "Warnings:", ...result.warnings] : []),
      ...(Object.keys(advancedAdjustedValues).length ? ["", "Advanced adjusted values:", ...Object.entries(advancedAdjustedValues).map(([label, value]) => `${label}: ${value}`)] : []),
    ].join("\n");
  };

  const copyResults = async () => {
    await navigator.clipboard?.writeText(resultText());
  };

  const handleSave = () => {
    if (hasErrors) return;
    onSave?.({
      summary: `${config.title} - ${Object.entries(result.values || {})[0]?.[1] || "calculation"}`,
      input: params,
      results: result,
      formulas: result.formulas || [],
      warnings: result.warnings || [],
      assumptions: result.assumptions || config.assumptions || [],
    });
  };

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header">
          <span className="calculator-result-label">
            {getCalculatorCategoryName(config.category)}
          </span>
          <h1>{config.title}</h1>
          <p>{config.description}</p>
          <button type="button" className="btn btn-secondary" onClick={toggleMode}>
            {mode === "beginner" ? "Beginner Mode" : "Advanced Mode"}
          </button>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <h3>Inputs</h3>
            <div className="calculator-input-grid">
              {config.fields.map((input) => (
                <label key={input.key} className="form-group">
                  {input.label}
                  {input.type === "select" ? (
                    <select
                      value={params[input.key] ?? ""}
                      onChange={(event) => updateField(input.key, event.target.value, input.type)}
                    >
                      {input.options.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={input.type || "number"}
                      step={input.step || "0.0001"}
                      value={params[input.key] ?? ""}
                      onChange={(event) => updateField(input.key, event.target.value, input.type || "number")}
                    />
                  )}
                </label>
              ))}
            </div>

            {mode === "advanced" && (
              <AdvancedOptionsPanel title="Advanced Decision Support">
                <div className="calculator-input-grid">
                  <label className="form-group">
                    Safety Factor
                    <input
                      type="number"
                      min="0.1"
                      step="0.05"
                      value={advancedParams.safetyFactor}
                      onChange={(event) => updateAdvancedField("safetyFactor", event.target.value)}
                    />
                  </label>
                  <label className="form-group">
                    Scrap / Rework / Stock Allowance %
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={advancedParams.allowancePercent}
                      onChange={(event) => updateAdvancedField("allowancePercent", event.target.value)}
                    />
                  </label>
                  <label className="form-group">
                    Gauge Resolution / Shop Increment
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={advancedParams.gaugeResolution}
                      placeholder="Optional"
                      onChange={(event) => updateAdvancedField("gaugeResolution", event.target.value)}
                    />
                  </label>
                  <label className="form-group">
                    Result Precision
                    <select
                      value={advancedParams.resultPrecision}
                      onChange={(event) => updateAdvancedField("resultPrecision", event.target.value)}
                    >
                      <option value="2">2 decimals</option>
                      <option value="3">3 decimals</option>
                      <option value="4">4 decimals</option>
                      <option value="5">5 decimals</option>
                    </select>
                  </label>
                </div>
              </AdvancedOptionsPanel>
            )}

            <div className="calculator-actions">
              <button type="button" className="btn btn-secondary" onClick={reset}>Reset</button>
              <button type="button" className="btn btn-secondary" onClick={copyResults}>Copy Results</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={hasErrors}>Save Calculation</button>
            </div>
          </div>

          <div className="calculator-panel">
            <h3>Results</h3>
            <ManufacturingDiagram toolId={toolId} params={params} />
            {hasErrors ? (
              <div className="warnings">
                <h4>More Data Required</h4>
                <ul>{result.errors.map((error) => <li key={error}>{error}</li>)}</ul>
              </div>
            ) : (
              <div className="calculator-results-grid">
                {Object.entries(result.values || {}).map(([label, value]) => (
                  <div key={label} className="calculator-result-card">
                    <span className="calculator-result-label">{label}</span>
                    <strong className="calculator-result-value">{value}</strong>
                  </div>
                ))}
                {mode === "advanced" && Object.entries(advancedAdjustedValues).map(([label, value]) => (
                  <div key={label} className="calculator-result-card">
                    <span className="calculator-result-label">{label}</span>
                    <strong className="calculator-result-value">{value}</strong>
                  </div>
                ))}
              </div>
            )}

            {mode === "advanced" && result.warnings?.length > 0 && (
              <div className="warnings" style={{ marginTop: "18px" }}>
                <h4>Warnings</h4>
                <ul>{result.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
              </div>
            )}

            {mode === "advanced" && (
              <section style={{ marginTop: "18px" }}>
                <h4>Formula Used</h4>
                <ul style={{ color: "var(--text-secondary)", paddingLeft: "18px" }}>
                  {(result.formulas || []).map((formula) => <li key={formula}>{formula}</li>)}
                </ul>
              </section>
            )}

            {mode === "advanced" && (
              <>
                <AssumptionsPanel assumptions={result.assumptions || config.assumptions || ["Verify calculator output against the print, material condition, tooling data, and shop standard before production."]} />
                <CalculationTracePanel trace={advancedTrace} />
                <ShopNotesPanel notes={getAdvancedShopNotes(config, advancedParams)} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { PROFESSIONAL_CALCULATOR_LABELS };
export default ProfessionalCalculator;
